import type { ChangeEvent } from 'react'

import { addressFuncs, validateEmailInput } from '@fv/client-core'
import type { ClientAddressLookupResult, FormBillTo } from '@fv/client-types'
import type { Workflow } from '@fv/models'
import { formatPhone } from '@fv/models/core'

import { AddressInput } from '@/components/inputs/AddressInput'
import { ContactPhoneInput } from '@/components/inputs/ContactPhoneInput'
import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'
import type { FormSectionProps } from '@/components/inputs/types'

import { AddressBookButton } from '../addresses/AddressBookButton'

const getInputName = (name: string) => `billTo-${name}`

type Props = FormSectionProps & {
  autoFocus?: boolean
  nameLabel?: string
  isTemplate?: boolean
  required?: boolean
  requireEmail?: boolean
  requirePhone?: boolean
  setValues: (values: Partial<FormBillTo>) => void
  values: FormBillTo
  workflow: Workflow
}

export const BillToForm = ({
  autoFocus = false,
  disabled,
  isTemplate = false,
  required = true,
  requireEmail = false,
  requirePhone = false,
  setValues,
  values,
  workflow,
}: Props) => {
  if (!required) return null

  const fieldsRequired = !isTemplate
  const hasAddressValue = [
    values.company,
    values.address,
    values.city,
    values.state,
    values.postalCode,
  ].some(v => v?.trim())
  const addressFieldsRequired = fieldsRequired || hasAddressValue

  function getInputProps(
    name: keyof FormBillTo,
    inputProps?: { autoFocus?: boolean },
  ) {
    return {
      disabled,
      name: getInputName(name),
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        if (name === 'contactEmail') {
          validateEmailInput(e.target)
        }

        setValues({ [name]: e.target.value })
      },
      value: values[name] ?? '',
      ...inputProps,
    }
  }

  function selectAddress(x: ClientAddressLookupResult | null) {
    if (!x) {
      setValues({
        city: '',
        country: values.country || 'us',
        postalCode: '',
        state: '',
      })
      return
    }

    setValues({
      address: x.address || '',
      address2: x.address2 || '',
      adminArea3: x.adminArea3,
      city: x.city || '',
      company: x.company || values.company,
      contactEmail: x.contactEmail || '',
      contactName: x.contactName || '',
      contactPhone: formatPhone(x.contactPhone) || '',
      contactPhoneExt: x.contactPhoneExt || '',
      country: x.country || values.country,
      postalCode: x.postalCode || '',
      state: x.state || '',
    })
  }

  function selectCityStateZip(x: ClientAddressLookupResult | null) {
    // Don't wipe other fields if the user selects a city/state/zip that doesn't have an address
    if (x && !x._id) {
      setValues({
        adminArea3: x.adminArea3,
        city: x.city || '',
        country: x.country || values.country,
        postalCode: x.postalCode || '',
        state: x.state || '',
      })
      return
    }

    selectAddress(x)
  }

  return (
    <>
      <InputGroupWrapper
        className="basis-1/3"
        label="Bill to company"
        name={getInputName('company')}
        required={addressFieldsRequired}
      >
        <AddressBookButton
          config={{ context: workflow, type: 'billing' }}
          disabled={disabled}
          setAddress={selectAddress}
        />
        <AddressInput
          allowCustomInput
          autoFocus={autoFocus}
          formatOptionLabel={addressFuncs.companyAddress}
          formatAddress={x => x.company ?? values.company}
          includeAddressBook
          includeAtlas={false}
          isClearable={false}
          location={{
            ...values,
            country: values.country || 'us',
          }}
          addressType="billing"
          name={getInputName('company')}
          onInputChange={company => setValues({ company })}
          onLocationChange={selectAddress}
          placeholder="Company name"
          required={addressFieldsRequired}
        />
      </InputGroupWrapper>

      <InputGroup
        className="basis-1/3"
        inputProps={getInputProps('address')}
        inputType="text"
        label="Bill to address"
        required={addressFieldsRequired}
      />

      <InputGroup
        className="basis-1/3"
        inputProps={getInputProps('address2')}
        inputType="text"
        label="Address 2"
      />

      <InputGroupWrapper
        className="basis-1/3"
        label="Bill to city, state, zip, country"
        name={getInputName('cityStateZipCountry')}
        required={addressFieldsRequired}
      >
        <AddressInput
          formatOptionLabel={addressFuncs.companyAddress}
          formatAddress={addressFuncs.cityStateZip}
          addressType="billing"
          context={workflow}
          includeAddressBook
          location={{
            ...values,
            country: values.country || 'us',
          }}
          name={getInputName('cityStateZipCountry')}
          onLocationChange={selectCityStateZip}
          readOnly={disabled}
          required={addressFieldsRequired}
        />
      </InputGroupWrapper>

      <InputGroup
        className="basis-2/12"
        inputProps={getInputProps('contactName')}
        inputType="text"
        label="Name"
        required={fieldsRequired}
      />

      <InputGroup
        className="basis-3/12"
        inputProps={getInputProps('contactEmail')}
        inputType="text"
        label="Email"
        required={fieldsRequired && requireEmail}
      />

      <ContactPhoneInput
        className="basis-3/12"
        disabled={disabled}
        label="Phone"
        name={getInputName('contactPhone')}
        onChange={setValues}
        required={fieldsRequired && requirePhone}
        values={values}
      />
    </>
  )
}
